import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Truck, Mail, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TXT = {
  fr: {
    loading: "Chargement...",
    not_found: "Ce lien n'est plus disponible.",
    title: "Votre sélection de chariots élévateurs",
    greeting: "Bonjour",
    intro: "Voici les chariots que nous avons sélectionnés pour vous",
    contact: "Contactez-nous",
    specs: "Spécifications",
    condition: "État",
    engine: "Moteur",
    capacity: "Capacité",
    mast: "Mât",
    tires: "Pneus",
    drive: "Traction",
    year: "Année",
    hours: "Heures",
    price: "Prix",
    new: "Neuf",
    used: "Usagé",
  },
  en: {
    loading: "Loading...",
    not_found: "This link is no longer available.",
    title: "Your forklift selection",
    greeting: "Hi",
    intro: "Here are the forklifts we selected for you",
    contact: "Contact us",
    specs: "Specifications",
    condition: "Condition",
    engine: "Engine",
    capacity: "Capacity",
    mast: "Mast",
    tires: "Tires",
    drive: "Drive",
    year: "Year",
    hours: "Hours",
    price: "Price",
    new: "New",
    used: "Used",
  },
};

export default function SharedMatchesPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/public/share/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [token]);

  const lang = data?.language === "en" ? "en" : "fr";
  const t = TXT[lang];

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        {t.loading}
      </div>
    );
  if (error || !data)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Truck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-700 font-medium">{TXT.fr.not_found}</p>
        </div>
      </div>
    );

  return (
    <div data-testid="shared-matches-page" className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-yellow-400 border-b-4 border-yellow-500">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-900 rounded-sm flex items-center justify-center">
              <Truck className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-gray-900">Fork Finder</h1>
              <p className="text-xs text-gray-900">par Gestión Farcar</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        <div className="mb-8">
          <p className="text-sm text-gray-500 mb-1">
            {t.greeting} {data.client_name},
          </p>
          <h2 className="font-heading text-3xl font-bold text-gray-900 mb-2">{t.title}</h2>
          <p className="text-gray-600">{t.intro} ({data.forklifts.length}).</p>
        </div>

        <div className="space-y-6">
          {data.forklifts.map((f) => {
            const imgUrl = f.image_url
              ? f.image_url.startsWith("/api/")
                ? `${process.env.REACT_APP_BACKEND_URL}${f.image_url}`
                : f.image_url
              : null;
            return (
              <div
                key={f.id}
                data-testid={`shared-forklift-${f.id}`}
                className="bg-white border border-gray-200 rounded-sm overflow-hidden hover:shadow-md transition-shadow"
              >
                <div className="grid md:grid-cols-5 gap-0">
                  <div className="md:col-span-2 bg-gray-100 flex items-center justify-center min-h-[240px]">
                    {imgUrl ? (
                      <img src={imgUrl} alt={`${f.brand} ${f.model}`} className="w-full h-full object-cover" />
                    ) : (
                      <Truck className="w-20 h-20 text-gray-300" />
                    )}
                  </div>
                  <div className="md:col-span-3 p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-heading text-2xl font-bold text-gray-900">
                          {f.brand} {f.model}
                        </h3>
                        <div className="flex items-center gap-2 mt-2">
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                              f.condition === "new"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {f.condition === "new" ? t.new : t.used}
                          </span>
                          {f.year && <span className="text-xs text-gray-500">{f.year}</span>}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-mono text-2xl font-bold text-gray-900">
                          ${f.price?.toLocaleString()}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                      {[
                        { l: t.engine, v: f.engine_type, cap: true },
                        { l: t.capacity, v: `${f.capacity_lbs?.toLocaleString()} lbs` },
                        { l: t.mast, v: `${f.mast_stages}` },
                        { l: t.tires, v: f.tire_type, cap: true },
                        { l: t.drive, v: (f.drive || "").toUpperCase() },
                        f.hours_used != null
                          ? { l: t.hours, v: `${f.hours_used?.toLocaleString()}` }
                          : null,
                      ]
                        .filter(Boolean)
                        .map((item, i) => (
                          <div key={i} className="bg-gray-50 rounded-sm px-3 py-2">
                            <p className="text-[10px] uppercase tracking-wide text-gray-500 font-bold">
                              {item.l}
                            </p>
                            <p className={`text-sm text-gray-900 font-medium ${item.cap ? "capitalize" : ""}`}>
                              {item.v}
                            </p>
                          </div>
                        ))}
                    </div>

                    {(lang === "fr" ? f.description_fr : f.description_en) && (
                      <p className="mt-4 text-sm text-gray-600 border-t border-gray-100 pt-3">
                        {lang === "fr" ? f.description_fr : f.description_en}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Contact footer */}
        <div className="mt-12 bg-gray-900 text-white rounded-sm p-8 text-center">
          <h3 className="font-heading text-xl font-bold mb-4">{t.contact}</h3>
          <div className="flex items-center justify-center gap-6 text-sm">
            <a href="tel:5149734754" className="inline-flex items-center gap-2 hover:text-yellow-400">
              <Phone className="w-4 h-4" /> 514-973-4754
            </a>
            <a href="mailto:contact@forkfinder.ca" className="inline-flex items-center gap-2 hover:text-yellow-400">
              <Mail className="w-4 h-4" /> contact@forkfinder.ca
            </a>
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">Fork Finder — Gestión Farcar</p>
      </main>
    </div>
  );
}
